import { useMemo, useState } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import WizardChrome, {
    IReceiptLine,
} from 'pages/loans/manager/grant/components/WizardChrome'
import StickyAction from 'pages/loans/manager/grant/components/StickyAction'
import DotsWhiteLoading from 'components/loadings/dotsWhiteLoading'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import { toman } from 'utilities/helper/format'
import {
    MAX_REPAYMENTS,
    MIN_INSTALLMENT_AMOUNT,
    MIN_LOAN_AMOUNT,
} from 'pages/loans/add/utilities'
import useSetRequestedLoan from 'pages/loans/add/hooks/useSetRequestedLoan'
import useRequestDraft from './useRequestDraft'
import StepWho from './steps/StepWho'
import StepAmount from './steps/StepAmount'
import StepTerms from './steps/StepTerms'
import StepReview from './steps/StepReview'

/**
 * درخواست وام, as a wizard.
 *
 * The old screen (`pages/loans/add`) asked everything at once: amount,
 * instalment count, a فوریت dropdown, a note, a guarantor picker and a bank
 * card modal, stacked in one form. It is still routed — the accept-a-request
 * flow links into it and cached bundles still navigate there — but nothing
 * points here from the app any more.
 *
 * Two things this adds beyond the layout:
 *
 * - **A manager can file for somebody else.** Android has always allowed it
 *   (`poolship_id` on the create endpoint); the PWA had no way to say it, so
 *   members who asked in person went unrecorded. Managers get a WHO step;
 *   ordinary members never see it, because the backend refuses a non-manager
 *   naming anyone but themselves.
 * - **The step lives in `?step=`**, so the phone's back button walks back
 *   through the questions instead of abandoning the flow and discarding
 *   every answer.
 */
export default function LoanRequestWizard() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { mutate, isLoading } = useSetRequestedLoan()
    const [showGuarantorPicker, setShowGuarantorPicker] = useState(false)

    const draft = useRequestDraft(id)

    // Read from the fund rather than passed in: this URL is bookmarkable
    // and reloadable, and router state does not survive that. Getting it
    // wrong in the optimistic direction would show an ordinary member a WHO
    // step whose every answer the backend then refuses.
    const { moneyPoolManagement } = useGetMoneyPoolManagement(id)
    const role = moneyPoolManagement?.poolship?.role
    const isManager = role === 'mgr' || role === 'own'
    // The caller's own membership, so «برای خودم» is one tap. The query
    // parameter is the fast path; the fund payload is the fallback that
    // survives a reload.
    const selfPoolshipId =
        Number(searchParams.get('poolship')) ||
        moneyPoolManagement?.poolship?.id ||
        undefined
    const profile = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('profile') || '{}')
        } catch {
            return {}
        }
    }, [])
    const self = selfPoolshipId
        ? {
              poolship_id: selfPoolshipId,
              name: `${profile?.first_name ?? ''} ${
                  profile?.last_name ?? ''
              }`.trim(),
          }
        : undefined

    // An ordinary member has exactly one possible answer to «برای کی؟», so
    // the step does not exist for them and the numbering shifts by one.
    const steps = isManager
        ? (['who', 'amount', 'terms', 'review'] as const)
        : (['amount', 'terms', 'review'] as const)
    const totalSteps = steps.length

    const rawStep = Number(searchParams.get('step')) || 1
    const step = Math.min(Math.max(rawStep, 1), totalSteps)
    const current = steps[step - 1]

    const goTo = (next: number) => {
        const params = new URLSearchParams(searchParams)
        params.set('step', String(Math.min(Math.max(next, 1), totalSteps)))
        setSearchParams(params)
    }

    const target = isManager ? draft.target : self
    const isForSomeoneElse = Boolean(
        isManager &&
            draft.target &&
            selfPoolshipId &&
            draft.target.poolship_id !== selfPoolshipId
    )

    const amountNumber = Number(draft.amount) || 0
    const repaymentsNumber = Number(draft.repayments) || 0
    const perInstallment =
        repaymentsNumber > 0 ? Math.round(amountNumber / repaymentsNumber) : 0

    // The same three limits Android's preRequestCheck enforces. The backend
    // only rejects amount<=0 / num_repayments<=0, so without these the PWA
    // happily creates 50 instalments of 1,000 تومان.
    const amountError =
        draft.amount !== '' && amountNumber < MIN_LOAN_AMOUNT
            ? `حداقل مبلغ وام ${MIN_LOAN_AMOUNT.toLocaleString(
                  'fa-IR'
              )} تومان است`
            : undefined

    const termsError =
        draft.repayments === ''
            ? undefined
            : repaymentsNumber <= 0
            ? 'تعداد اقساط نمی‌تواند صفر باشد'
            : repaymentsNumber > MAX_REPAYMENTS
            ? `حداکثر تعداد اقساط ${MAX_REPAYMENTS.toLocaleString('fa-IR')} است`
            : perInstallment < MIN_INSTALLMENT_AMOUNT
            ? `مبلغ هر قسط نباید کمتر از ${MIN_INSTALLMENT_AMOUNT.toLocaleString(
                  'fa-IR'
              )} تومان باشد`
            : undefined

    const canContinue =
        current === 'who'
            ? Boolean(draft.target)
            : current === 'amount'
            ? amountNumber >= MIN_LOAN_AMOUNT
            : current === 'terms'
            ? repaymentsNumber > 0 && !termsError
            : Boolean(draft.cardId)

    const receipt: IReceiptLine[] = []
    const stepIndexOf = (name: string) => steps.indexOf(name as any) + 1
    if (isManager && draft.target && step > stepIndexOf('who')) {
        receipt.push({
            label: 'برای',
            value: draft.target.name,
            step: stepIndexOf('who'),
        })
    }
    if (amountNumber > 0 && step > stepIndexOf('amount')) {
        receipt.push({
            label: 'مبلغ',
            value: toman(amountNumber),
            step: stepIndexOf('amount'),
        })
    }
    if (repaymentsNumber > 0 && step > stepIndexOf('terms')) {
        receipt.push({
            label: 'اقساط',
            value: `${repaymentsNumber.toLocaleString('fa-IR')} قسط`,
            step: stepIndexOf('terms'),
        })
    }

    const titles: Record<string, { title: string; subtitle?: string }> = {
        who: {
            title: 'این درخواست برای کیست؟',
            subtitle:
                'می‌توانی برای خودت ثبت کنی یا برای عضوی که حضوری از تو درخواست وام کرده است.',
        },
        amount: { title: 'چه مبلغی لازم داری؟' },
        terms: { title: 'بازپرداخت و فوریت' },
        review: {
            title: 'یک بار مرور کن',
            subtitle: 'بعد از ثبت، مدیران صندوق درخواست را می‌بینند.',
        },
    }

    const submit = () => {
        if (!draft.cardId) {
            toast.error('لطفا حساب را انتخاب کنید')
            return
        }
        posthog.capture('loan_request_submitted', {
            moneypool_id: id,
            on_behalf_of_member: isForSomeoneElse,
            demand_priority: draft.priority,
            has_note: Boolean(draft.note.trim()),
            proposed_guarantor_count: draft.guarantors.length,
        })
        mutate(
            {
                amount: amountNumber,
                num_repayments: repaymentsNumber,
                demand_priority: draft.priority,
                user_description: draft.note,
                selected_card_number: draft.cardId,
                // Only sent when it differs from the caller: a member posting
                // their own poolship_id is harmless but pointless, and the
                // backend already defaults to the caller.
                poolship_id: target?.poolship_id,
                id: id ?? '',
                proposed_guarantors: draft.guarantors.map(
                    (item) => item.poolship_id
                ),
            } as any,
            {
                onSuccess: () => draft.clear(),
            } as any
        )
    }

    return (
        <WizardChrome
            flowTitle="درخواست وام"
            title={titles[current].title}
            subtitle={titles[current].subtitle}
            step={step}
            totalSteps={totalSteps}
            receipt={receipt}
            onBack={() => (step === 1 ? navigate(-1) : goTo(step - 1))}
            onJumpTo={goTo}
        >
            {current === 'who' && (
                <StepWho
                    moneypoolId={id}
                    value={draft.target}
                    self={self}
                    onPick={(picked) => {
                        draft.setTarget(picked)
                        goTo(step + 1)
                    }}
                />
            )}

            {current === 'amount' && (
                <StepAmount
                    value={draft.amount}
                    onChange={draft.setAmount}
                    error={amountError}
                />
            )}

            {current === 'terms' && (
                <StepTerms
                    amount={draft.amount}
                    repayments={draft.repayments}
                    priority={draft.priority}
                    onRepayments={draft.setRepayments}
                    onPriority={draft.setPriority}
                    error={termsError}
                />
            )}

            {current === 'review' && (
                <StepReview
                    moneypoolId={id}
                    target={target}
                    isForSomeoneElse={isForSomeoneElse}
                    amount={draft.amount}
                    repayments={draft.repayments}
                    priority={draft.priority}
                    note={draft.note}
                    onNote={draft.setNote}
                    guarantors={draft.guarantors}
                    onGuarantors={draft.setGuarantors}
                    cardId={draft.cardId}
                    onCard={draft.setCardId}
                    showGuarantorPicker={showGuarantorPicker}
                    onToggleGuarantorPicker={setShowGuarantorPicker}
                />
            )}

            {/* The WHO step commits on tap — a picked member IS the answer,
                so a second «ادامه» would be a tap that does nothing. */}
            {current !== 'who' && (
                <StickyAction
                    summary={
                        perInstallment > 0 && current !== 'review' ? (
                            <p className=" text-center text-[11px] text-text-secondary">
                                هر قسط حدود{' '}
                                <span className=" font-bold text-text-body">
                                    {perInstallment.toLocaleString('fa-IR')}
                                </span>{' '}
                                تومان
                            </p>
                        ) : undefined
                    }
                >
                    <button
                        type="button"
                        disabled={!canContinue || isLoading}
                        onClick={() =>
                            current === 'review' ? submit() : goTo(step + 1)
                        }
                        className=" w-full rounded-full bg-green-1 py-3 text-sm font-bold text-white disabled:opacity-50"
                    >
                        {isLoading ? (
                            <DotsWhiteLoading />
                        ) : current === 'review' ? (
                            'ثبت درخواست وام'
                        ) : (
                            'ادامه'
                        )}
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
